import React from 'react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  Loader2,
  X,
  RotateCcw,
  Eye
} from 'lucide-react';

// Toast types matching the design system
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

// Toast position options
export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

// Toast action button interface
export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'primary' | 'ghost';
}

// Enhanced toast props
export interface EnhancedToastProps {
  type?: ToastType;
  title?: string;
  description?: string;
  actions?: ToastAction[];
  duration?: number;
  dismissible?: boolean;
  showProgress?: boolean;
  icon?: React.ReactNode;
}

// Toaster configuration props
export interface ToasterProps {
  /**
   * Position of the toast notifications
   * @default 'top-right' on desktop, 'bottom-center' on mobile
   */
  position?: ToastPosition;

  /**
   * Whether to use mobile-friendly position (bottom-center) on small screens
   * @default true
   */
  mobileBottomPosition?: boolean;
}

// Get icon for toast type
const getIconForType = (type: ToastType): React.ReactNode => {
  switch (type) {
    case 'success':
      return <CheckCircle className="h-5 w-5 text-success-600" />;
    case 'error':
      return <XCircle className="h-5 w-5 text-error-600" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-warning-600" />;
    case 'info':
      return <Info className="h-5 w-5 text-primary-600" />;
    case 'loading':
      return <Loader2 className="h-5 w-5 text-primary-600 animate-spin" />;
    default:
      return null;
  }
};

// Get background color for toast type
const getStylesForType = (type: ToastType): string => {
  switch (type) {
    case 'success':
      return 'bg-success-50 border-success-200 text-success-900';
    case 'error':
      return 'bg-error-50 border-error-200 text-error-900';
    case 'warning':
      return 'bg-warning-50 border-warning-200 text-warning-900';
    case 'info':
      return 'bg-primary-50 border-primary-200 text-primary-900';
    case 'loading':
      return 'bg-cream-50 border-cream-300 text-espresso-900';
    default:
      return 'bg-white border-cream-200 text-espresso-900';
  }
};

// Enhanced Toast Component
export const Toast: React.FC<EnhancedToastProps> = ({
  type = 'info',
  title,
  description,
  actions,
  icon,
}) => {
  const displayIcon = icon || getIconForType(type);

  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${getStylesForType(type)}`}>
      {displayIcon && (
        <div className="flex-shrink-0 mt-0.5">
          {displayIcon}
        </div>
      )}

      <div className="flex-1 min-w-0">
        {title && (
          <div className="font-semibold text-sm mb-1">
            {title}
          </div>
        )}
        {description && (
          <div className="text-sm opacity-90">
            {description}
          </div>
        )}

        {actions && actions.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={`
                  text-xs font-medium px-3 py-1.5 rounded-md transition-colors
                  ${action.variant === 'primary'
                    ? 'bg-terracotta-500 text-white hover:bg-terracotta-600'
                    : action.variant === 'ghost'
                    ? 'hover:bg-black/5'
                    : 'bg-white/50 hover:bg-white/80'
                  }
                `}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Enhanced Toaster component with custom styling and responsive positioning
 *
 * On mobile devices (< 640px), toasts appear at the bottom for better reachability.
 * On desktop, they appear at the configured position (default: top-right).
 */
export const Toaster: React.FC<ToasterProps> = ({
  position = 'top-right',
  mobileBottomPosition = true,
}) => {
  // Detect mobile viewport for responsive positioning
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Initial check
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use bottom-center on mobile for better thumb reachability
  const effectivePosition: ToastPosition =
    mobileBottomPosition && isMobile ? 'bottom-center' : position;

  return (
    <SonnerToaster
      position={effectivePosition}
      expand={true}
      richColors={false}
      closeButton={true}
      duration={4000}
      gap={12}
      toastOptions={{
        unstyled: false,
        classNames: {
          toast: 'rounded-lg shadow-lg border backdrop-blur-sm motion-reduce:transition-none motion-reduce:animate-none',
          title: 'font-semibold text-sm',
          description: 'text-sm opacity-90',
          actionButton: 'bg-terracotta-500 text-white hover:bg-terracotta-600 px-3 py-1.5 rounded-md text-xs font-medium min-h-[44px] min-w-[44px]',
          cancelButton: 'bg-cream-100 text-espresso-700 hover:bg-cream-200 px-3 py-1.5 rounded-md text-xs font-medium min-h-[44px] min-w-[44px]',
          closeButton: 'bg-white/50 hover:bg-white/80 border-0 min-h-[44px] min-w-[44px]',
          success: 'bg-success-50 border-success-200 text-success-900',
          error: 'bg-error-50 border-error-200 text-error-900',
          warning: 'bg-warning-50 border-warning-200 text-warning-900',
          info: 'bg-primary-50 border-primary-200 text-primary-900',
          loading: 'bg-cream-50 border-cream-300 text-espresso-900',
        },
      }}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const toast = {
  // Success toast
  success: (message: string, options?: { description?: string; duration?: number; action?: ToastAction }) => {
    return sonnerToast.success(message, {
      description: options?.description,
      duration: options?.duration || 4000,
      icon: <CheckCircle className="h-5 w-5" />,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick,
      } : undefined,
    });
  },

  // Error toast
  error: (message: string, options?: { description?: string; duration?: number; action?: ToastAction }) => {
    return sonnerToast.error(message, {
      description: options?.description,
      duration: options?.duration || 6000,
      icon: <XCircle className="h-5 w-5" />,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick,
      } : undefined,
    });
  },

  // Warning toast
  warning: (message: string, options?: { description?: string; duration?: number; action?: ToastAction }) => {
    return sonnerToast.warning(message, {
      description: options?.description,
      duration: options?.duration || 5000,
      icon: <AlertTriangle className="h-5 w-5" />,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick,
      } : undefined,
    });
  },

  // Info toast
  info: (message: string, options?: { description?: string; duration?: number; action?: ToastAction }) => {
    return sonnerToast.info(message, {
      description: options?.description,
      duration: options?.duration || 4000,
      icon: <Info className="h-5 w-5" />,
      action: options?.action ? {
        label: options.action.label,
        onClick: options.action.onClick,
      } : undefined,
    });
  },

  // Loading toast
  loading: (message: string, options?: { description?: string }) => {
    return sonnerToast.loading(message, {
      description: options?.description,
      icon: <Loader2 className="h-5 w-5 animate-spin" />,
    });
  },

  // Promise-based toast (loading → success/error)
  promise: <T,>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
      description?: {
        loading?: string;
        success?: string;
        error?: string;
      };
    }
  ) => {
    return sonnerToast.promise(promise, {
      loading: options.loading,
      success: options.success,
      error: options.error,
      description: options.description as never,
    });
  },

  // Custom toast with full control
  custom: (component: React.ReactNode, options?: { duration?: number; dismissible?: boolean }) => {
    return sonnerToast.custom(component as never, {
      duration: options?.duration,
      dismissible: options?.dismissible !== false,
    });
  },

  // Dismiss a specific toast
  dismiss: (toastId?: string | number) => {
    return sonnerToast.dismiss(toastId);
  },

  // Dismiss all toasts
  dismissAll: () => {
    return sonnerToast.dismiss();
  },
};

// eslint-disable-next-line react-refresh/only-export-components
export const toastHelpers = {
  // Network error with retry
  networkError: (retry?: () => void) => {
    return toast.error('Connection failed', {
      description: 'Unable to reach the server. Please check your internet connection.',
      duration: 6000,
      action: retry ? {
        label: 'Retry',
        onClick: retry,
      } : undefined,
    });
  },

  // Validation error
  validationError: (message: string) => {
    return toast.warning('Validation Error', {
      description: message,
      duration: 5000,
    });
  },

  // Unauthorized access
  unauthorized: () => {
    return toast.error('Access Denied', {
      description: 'You do not have permission to perform this action.',
      duration: 5000,
    });
  },

  // Session expired
  sessionExpired: (onLogin?: () => void) => {
    return toast.warning('Session Expired', {
      description: 'Your session has expired. Please log in again.',
      duration: 7000,
      action: onLogin ? {
        label: 'Log In',
        onClick: onLogin,
      } : undefined,
    });
  },

  // Copy to clipboard
  copied: (what?: string) => {
    return toast.success('Copied', {
      description: what ? `${what} copied to clipboard` : 'Copied to clipboard',
      duration: 2000,
    });
  },
};

export default Toast;
